/**
 * VideoSnapshot 类 - 用于从视频文件中提取截图
 */
class VideoSnapshot {
  /**
   * 创建VideoSnapshot实例
   * @param {Blob|File} videoBlob - 视频文件Blob或File对象
   */
  constructor(videoBlob) {
    this.videoUrl = URL.createObjectURL(videoBlob);
    this.video = document.createElement('video');
    this.video.src = this.videoUrl;
    this.video.muted = true;
    this.video.crossOrigin = "anonymous";
  }

  /**
   * 获取视频的特定时间点的截图
   * @param {number|string} time - 时间点（秒）或预设位置 ('start', 'middle', 'end')
   * @param {string} format - 输出格式，默认为'image/png'
   * @param {number} quality - 图像质量，默认为0.9，设置为1.0时保持原始质量
   * @returns {Promise<{dataUrl: string, width: number, height: number, timestamp: number}>} 返回图像数据
   */
  async takeSnapshot(time = 0, format = 'image/png', quality = 0.9) {
    return new Promise((resolve, reject) => {
      try {
        const videoElement = this.video;
        
        const handleVideoReady = () => {
          const canvas = document.createElement('canvas');
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;
          
          const context = canvas.getContext('2d');
          if (!context) {
            throw new Error('无法创建canvas上下文');
          }
          
          context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL(format, quality);
          
          // 记录当前截图的相关信息
          const result = {
            dataUrl,
            width: canvas.width,
            height: canvas.height,
            timestamp: videoElement.currentTime
          };
          
          videoElement.pause();
          resolve(result);
        };

        // 设置视频的时间点
        if (typeof time === 'string') {
          // 使用预设位置
          videoElement.addEventListener('loadedmetadata', () => {
            const duration = videoElement.duration;
            const timePositions = {
              start: 0,
              middle: duration / 2,
              end: duration * 0.95 // 使用95%而不是100%以确保有画面
            };
            videoElement.currentTime = timePositions[time] || 0;
          }, { once: true });
        } else {
          // 使用具体秒数
          videoElement.addEventListener('loadedmetadata', () => {
            videoElement.currentTime = time;
          }, { once: true });
        }

        // 当视频准备好当前时间帧时，捕获截图
        videoElement.addEventListener('seeked', handleVideoReady, { once: true });
        
        // 错误处理
        videoElement.addEventListener('error', (e) => {
          reject(new Error(`视频加载失败: ${e.message}`));
        }, { once: true });
        
        // 预加载视频
        videoElement.load();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 压缩图像数据
   * @param {string} dataUrl - 原始图像的Data URL
   * @param {number} maxWidth - 最大宽度，默认为1280
   * @param {number} quality - 压缩质量，默认为0.8
   * @returns {Promise<string>} 返回压缩后的Data URL
   */
  static async compressImage(dataUrl, maxWidth = 1280, quality = 0.8) {
    return new Promise((resolve, reject) => {
      try {
        const image = new Image();
        image.onload = () => {
          let width = image.width;
          let height = image.height;
          
          // 如果图像宽度超过最大限制，按比例缩小
          if (width > maxWidth) {
            const ratio = maxWidth / width;
            width = maxWidth;
            height = Math.floor(height * ratio);
          }
          
          // 创建画布并绘制缩小后的图像
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(image, 0, 0, width, height);
          
          // 导出为压缩的图像格式
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedDataUrl);
        };
        
        image.onerror = () => reject(new Error('图像加载失败'));
        image.src = dataUrl;
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 获取视频的维度信息
   * @returns {Promise<{width: number, height: number, duration: number}>} 返回视频的宽高和时长
   */
  async getMetadata() {
    return new Promise((resolve, reject) => {
      const videoElement = this.video;
      
      if (videoElement.readyState >= 1) {
        resolve({
          width: videoElement.videoWidth,
          height: videoElement.videoHeight,
          duration: isNaN(videoElement.duration) ? 0 : videoElement.duration
        });
        return;
      }
      
      videoElement.addEventListener('loadedmetadata', () => {
        resolve({
          width: videoElement.videoWidth,
          height: videoElement.videoHeight,
          duration: isNaN(videoElement.duration) ? 0 : videoElement.duration
        });
      }, { once: true });
      
      videoElement.addEventListener('error', (e) => {
        reject(new Error(`获取视频元数据失败: ${e.message || '未知错误'}`));
      }, { once: true });
      
      videoElement.load();
    });
  }

  /**
   * 清理资源
   */
  destroy() {
    if (this.videoUrl) {
      URL.revokeObjectURL(this.videoUrl);
      this.videoUrl = null;
    }
    
    if (this.video) {
      this.video.src = '';
      this.video.load();
      this.video = null;
    }
  }
}

export default VideoSnapshot; 